import Octicon, { Plus } from '@githubprimer/octicons-react';
import CircleButton from 'components/shared/CircleButton';
import * as React from 'react';
import { Field, FieldRenderProps } from 'react-final-form';
import { Button, Input, Label } from 'reactstrap';
import { InputProps, ValidationMessages } from '.';

type TextListRenderProps = FieldRenderProps<any, HTMLInputElement> & InputProps;

/**
 * Represents a dynamically expanding list of single line text entries.
 *
 * @class TextList
 * @extends {(React.Component<FieldRenderProps & InputProps>)}
 */
class TextList extends React.Component<TextListRenderProps> {
	/** Parses a value into a string array representing the lines of the text list. */
	private parse = (serverSetting: string | undefined) => {
		if (!serverSetting) {
			return [];
		}

		let lines: string[];

		try {
			lines = JSON.parse(serverSetting);
		} catch {
			return [];
		}

		if (!Array.isArray(lines)) {
			return [];
		}

		return lines;
	};

	/** Alerts React-Final-Form that the text list values have changed */
	private updateValue = (lines: string[]) => {
		const { input } = this.props;

		input.onChange(JSON.stringify(lines));
	};

	/** Deletes a text line row */
	private deleteRow = (index: number) => {
		// Deletion will cause validation errors to become misaligned.
		// One fix for this is to send an object instead of an array so delete errors reference a property that doesn't exist
		// An ideal fix would be manipulating meta.submitError but I am not sure this is possible
		const newLines = this.parse(this.props.input.value);
		newLines.splice(index, 1);
		this.updateValue(newLines);
	};

	/** Event fired when a text line row changes */
	private onRowChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
		const newValue = e.currentTarget.value;
		this.updateRow(index, newValue);
	};

	/** Updates a text line row */
	private updateRow = (index: number, text: string) => {
		const newLines = this.parse(this.props.input.value);
		newLines[index] = text;
		this.updateValue(newLines);
	};

	/** Adds a text line row to the end of the list */
	private addRow = () => {
		const newLines = [...this.parse(this.props.input.value), ''];
		this.updateValue(newLines);
	};

	public render() {
		const inputName = this.props.input.name;
		const { readOnly, label } = this.props;
		const lines = this.parse(this.props.input.value);

		return (
			<div className="roc-list roc-list--full-width" id={inputName} role="table">
				<div className="roc-list__head" role="rowgroup">
					<div className="roc-list__row" role="row">
						<div className="roc-list__cell roc-list__cell--label" role="cell">
							<Label>{label}</Label>
						</div>
						<div className="roc-list__cell roc-list__cell--actions" role="cell"></div>
					</div>
				</div>
				<div className="roc-list__body" role="rowgroup">
					{lines.map((value, index) => {
						return (
							<Field key={`${inputName}.${index}`} name={`${inputName}.${index}`}>
								{({ meta }) => (
									<>
										<div className="roc-list__row" role="row">
											<div className="roc-list__cell roc-list__cell--label" role="cell">
												<Input
													id={`${inputName}.${index}.Input`}
													type="text"
													value={value}
													onChange={(e) => this.onRowChange(e, index)}
													readOnly={readOnly}
												/>
											</div>
											{!readOnly ? (
												<div className="roc-list__cell roc-list__cell--actions" role="cell">
													<CircleButton onClick={() => this.deleteRow(index)} />
												</div>
											) : null}
										</div>
										<ValidationMessages meta={meta} id={`${inputName}.${index}.Input`} />
									</>
								)}
							</Field>
						);
					})}
				</div>
				{!readOnly ? (
					<div className="form-group__actions">
						<Button color="primary" className="btn-flush-bottom" onClick={() => this.addRow()}>
							<Octicon icon={Plus} ariaLabel="Plus sign" /> Add new
						</Button>
					</div>
				) : null}
			</div>
		);
	}
}

export default TextList;
